import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Heart, MessageCircle, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MatchesPage = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API_URL}/matches`);
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="matches-page">
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-semibold mb-2">Your Matches</h1>
          <p className="text-muted-foreground">Connect with people who matched with you</p>
        </div>

        {matches.length === 0 ? (
          <Card className="rounded-2xl border-border/50 shadow-soft">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-rose-400" />
              </div>
              <h3 className="text-xl font-serif font-medium mb-2">No matches yet</h3>
              <p className="text-muted-foreground mb-6">
                Start discovering profiles to find your compatible matches.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {matches.map((match) => (
              <Card 
                key={match.match_id}
                className="rounded-2xl border-border/50 shadow-soft cursor-pointer hover-lift transition-all overflow-hidden"
                onClick={() => navigate(`/chat/${match.match_id}`)}
                data-testid={`match-card-${match.match_id}`}
              >
                <div className="relative">
                  {/* Photo */}
                  <div className="aspect-square bg-muted">
                    {match.user.profile_photo_url ? (
                      <img 
                        src={match.user.profile_photo_url}
                        alt={match.user.display_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Avatar className="w-24 h-24">
                          <AvatarFallback className="text-3xl bg-primary/10 text-primary">
                            {match.user.display_name?.charAt(0)?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    )}
                  </div>

                  {/* Compatibility Badge */}
                  <Badge className="absolute top-3 right-3 bg-white/90 text-foreground rounded-full">
                    {Math.round(match.compatibility_score)}% Match
                  </Badge>
                </div>

                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">{match.user.display_name}</h3>
                      {match.user.location_city && (
                        <p className="text-sm text-muted-foreground">
                          {match.user.location_city}, {match.user.location_state}
                        </p>
                      )}
                    </div>
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MatchesPage;
